import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { User } from '../../@types/global';
import { makeAPIRequestReactQuery } from '../../react-query/api';
import { endpoints } from '../../react-query/endpoints';

/**
 * Invalidates and refetches queries related to rerun AI analysis.
 * This should be called after a successful rerun to immediately update
 * latest images, visits, and visit summaries.
 */
export const invalidateRerunQueries = async (queryClient: QueryClient) => {
  // Invalidate visits and visitSummaries queries
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [endpoints.visits],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [endpoints.visitSummaries],
      refetchType: 'all',
    }),
  ]);

  // ! IMPORTANT: This must be last, otherwise the latest images will not be invalidated, idk why
  // Then, invalidate latestImages queries first (patient + latestImages)
  await queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === endpoints.patient &&
        key[1] === endpoints.latestImages
      );
    },
    refetchType: 'all',
  });
};

export const useRerunAi = (
  images: { imageId: string; rotation?: number; mirrorImage?: boolean }[],
  estimatedVisitId?: number,
) => {
  const queryClient = useQueryClient();
  const path = '/v2/analyze-image/rerun-analysis';
  const { isLoading, mutateAsync } = useMutation(
    [path],
    () =>
      makeAPIRequestReactQuery(path, undefined, 'POST', {
        data: {
          images,
          estimatedVisitId,
        },
      }),
    {
      onSuccess: async () => {
        await invalidateRerunQueries(queryClient);
      },
    },
  );
  return { isLoading, mutateAsync };
};

export const useRerunAiForNewCariesModel = (estimatedVisitId: number) => {
  const queryClient = useQueryClient();
  const path = `/fmx-summary/${estimatedVisitId}/caries-bins`;
  const user: User = useSelector((state: any) => state?.login?.user);

  const { isLoading, mutateAsync } = useMutation(
    [path],
    () =>
      makeAPIRequestReactQuery(path, undefined, 'POST', {
        data: {
          estimatedVisitId,
        },
        headers: {
          Org: user.primaryOrganizationId,
          Practice: user.primaryPracticeId,
        },
      }),
    {
      onSuccess: async () => {
        await invalidateRerunQueries(queryClient);
      },
    },
  );
  return { isLoading, mutateAsync };
};
